/**
 * The label list as an Excel sheet: every variable and item of the project,
 * one row each, with its shown label. Only the Shown label column can be
 * edited (the sheet is protected without a password), so a list downloaded,
 * edited and uploaded again names what it named. Pure: the Labels drawer
 * downloads what `buildLabelSheet` returns and writes what `planImport`
 * plans in one `setShownLabels`.
 */

import ExcelJS from "exceljs"
import { EXCEL_DEFAULT_FONT_NAME } from "utils/excel/exportTableModels"
import {
  ItemRow,
  ItemWord,
  LabelRow,
  takenNames,
  VariableRow,
} from "../components/Labels/labelRows"
import { LabelChange, labelKey, refusedShownLabel } from "../model/shownLabels"
import { labelTargetOf } from "../types/tabSpecDocument"
import { bookFileName } from "./tabBook"

export type SheetKind = "Variable" | "Category" | "Row" | "Cell" | "Iteration"

export interface LabelSheetRow {
  variable: string
  kind: SheetKind
  label: string
  /** "" when the item shows its raw label. */
  shown: string
}

export const LABEL_SHEET = "Labels"
export const LABEL_HEADINGS = ["Variable", "Kind", "Label", "Shown label"] as const

export const BAD_FILE =
  "This file is not a label list from DATA(Q). It needs the columns Variable, Kind, Label and Shown label."

const KIND_OF_WORD: Record<ItemWord, SheetKind> = {
  category: "Category",
  row: "Row",
  cell: "Cell",
  iteration: "Iteration",
}
const SHEET_KINDS: SheetKind[] = ["Variable", "Category", "Row", "Cell", "Iteration"]
const WIDTHS = [30, 12, 40, 40]
/** The column the user writes in (Shown label). */
const SHOWN_COLUMN = 4

const kindOfRow = (row: VariableRow | ItemRow): SheetKind =>
  row.kind === "variable" ? "Variable" : KIND_OF_WORD[row.word]

/** The variable and item rows of the drawer, in its order; gone rows and headings are left out. */
export const sheetRowsOf = (rows: LabelRow[]): LabelSheetRow[] =>
  rows.flatMap((r) =>
    r.kind === "variable" || r.kind === "item"
      ? [{ variable: r.target.variable, kind: kindOfRow(r), label: r.raw, shown: r.shown ?? "" }]
      : []
  )

/**
 * One sheet "Labels": the headings in bold on a frozen first row, then one
 * row per label. Every cell is locked except Shown label from row 2, and the
 * sheet is protected without a password: Excel lets the user edit that
 * column only, and anyone can lift the protection.
 */
export const buildLabelSheet = (rows: LabelSheetRow[]): ExcelJS.Workbook => {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet(LABEL_SHEET, { views: [{ state: "frozen", ySplit: 1 }] })
  WIDTHS.forEach((width, i) => {
    ws.getColumn(i + 1).width = width
  })
  LABEL_HEADINGS.forEach((heading, i) => {
    const cell = ws.getCell(1, i + 1)
    cell.value = heading
    cell.font = { name: EXCEL_DEFAULT_FONT_NAME, bold: true }
  })
  rows.forEach((r, i) => {
    const row = ws.getRow(i + 2)
    row.getCell(1).value = r.variable
    row.getCell(2).value = r.kind
    row.getCell(3).value = r.label
    const shown = row.getCell(SHOWN_COLUMN)
    shown.value = r.shown || null
    shown.protection = { locked: false }
  })
  // exceljs sets the protection before its promise resolves (it only hashes
  // a password, and there is none), so the workbook is ready on return.
  void ws.protect("", { selectLockedCells: true, selectUnlockedCells: true })
  return wb
}

/** The file name of the download: "<project> - <spec> - labels", as the tab book names its file. */
export const labelFileName = (projectName: string, specName: string): string =>
  [bookFileName(projectName, specName), "labels"].filter(Boolean).join(" - ")

/**
 * The rows of the first sheet of an uploaded file. Row 1 must hold the four
 * headings (trimmed, in any case, in any order); every cell is read as its
 * text. Empty rows are skipped; a row whose Kind is none of the five is
 * counted in `unknown` and not returned. A file that is not a workbook, or a
 * first sheet without the headings, gives the bad-file sentence.
 */
export const readLabelSheet = async (
  data: ArrayBuffer
): Promise<{ rows: LabelSheetRow[]; unknown: number } | { error: string }> => {
  const wb = new ExcelJS.Workbook()
  try {
    // A copy made here: the bytes of a file read in another realm (a test's
    // jsdom) are not an ArrayBuffer the zip reader recognises.
    await wb.xlsx.load(new Uint8Array(data).slice().buffer)
  } catch {
    return { error: BAD_FILE }
  }
  const ws = wb.worksheets[0]
  if (!ws) return { error: BAD_FILE }
  const columns = new Map<string, number>()
  ws.getRow(1).eachCell((cell, col) => {
    const heading = cell.text.trim().toLowerCase()
    if (!columns.has(heading)) columns.set(heading, col)
  })
  const at = LABEL_HEADINGS.map((h) => columns.get(h.toLowerCase()))
  if (at.some((col) => col === undefined)) return { error: BAD_FILE }
  const [variableAt, kindAt, labelAt, shownAt] = at as number[]

  const rows: LabelSheetRow[] = []
  let unknown = 0
  for (let r = 2; r <= ws.rowCount; r++) {
    const row = ws.getRow(r)
    const text = (col: number): string => row.getCell(col).text
    const variable = text(variableAt)
    const kindText = text(kindAt)
    const label = text(labelAt)
    const shown = text(shownAt)
    if (![variable, kindText, label, shown].some((t) => t.trim())) continue
    const kind = SHEET_KINDS.find((k) => k.toLowerCase() === kindText.trim().toLowerCase())
    if (!kind) unknown += 1
    else rows.push({ variable, kind, label, shown })
  }
  return { rows, unknown }
}

export interface ImportPlan {
  changes: LabelChange[]
  changed: number
  reset: number
  /** Sheet rows that name no variable or item of the project: counted, never stored. */
  unknown: number
  /**
   * Labels stored for a variable the data may have, read while the data is
   * not loaded: listed under "Waiting for the data" until it is.
   */
  waiting: number
  refused: { name: string; reason: string }[]
}

const WORD_OF_KIND: Record<Exclude<SheetKind, "Variable">, ItemWord> = {
  Category: "category",
  Row: "row",
  Cell: "cell",
  Iteration: "iteration",
}

/**
 * A sheet row as a row of a variable the data may have, read while the data
 * is not loaded: an item's Label is its option, or its instance id (a
 * variable of the data names its rows, cells and iterations by their
 * labels). Its shown label is the stored entry's (`stored`, by key), if any.
 * null when the row cannot name a label: an empty part, or a Variable row
 * whose Label is not its Variable.
 */
const waitingRowOf = (
  s: LabelSheetRow,
  stored: Map<string, string>
): VariableRow | ItemRow | null => {
  if (s.kind === "Variable") {
    const target = labelTargetOf({ variable: s.variable })
    if (!target || s.label !== s.variable) return null
    const key = labelKey(target)
    return {
      kind: "variable",
      key,
      target,
      raw: s.variable,
      shown: stored.get(key) ?? null,
      phrase: "",
      name: s.variable,
      within: null,
    }
  }
  const target = labelTargetOf(
    s.kind === "Category"
      ? { variable: s.variable, option: s.label }
      : { variable: s.variable, instance: s.label }
  )
  if (!target) return null
  const key = labelKey(target)
  return {
    kind: "item",
    key,
    target,
    raw: s.label,
    shown: stored.get(key) ?? null,
    word: WORD_OF_KIND[s.kind],
    variable: s.variable,
  }
}

/** A planned change of one row: `text` null for a reset; `reason` once it is refused. */
interface Decision {
  row: VariableRow | ItemRow
  text: string | null
  reason: string | null
}

/**
 * What an uploaded sheet changes. Each sheet row is matched on (Variable,
 * Kind, Label) against the drawer's rows (the first sheet row of an item
 * counts). While the data is not loaded (`dataLoaded` false), a row whose
 * Variable is none of the project's may name a variable of the data: it is
 * read as one (`waiting`), stored, and listed under "Waiting for the data";
 * any other row that names nothing of the project is counted in `unknown`
 * and never stored. A blank Shown label, or the raw label itself, is a reset
 * when the item has a shown label, nothing otherwise; the same shown label as
 * now is nothing. Any other text is checked as a typed one
 * (`refusedShownLabel`); an item's against the names the other items of its
 * variable and word keep or show after the import: every planned name counts
 * first, each is checked in sheet order, a refused one goes back under the
 * name it has, and any accepted name that then clashes with it is refused in
 * turn, until none clashes. A variable in a clash sentence is named as it is
 * shown after the import.
 */
export const planImport = (
  sheet: LabelSheetRow[],
  rows: LabelRow[],
  dataLoaded = true
): ImportPlan => {
  const idOf = (variable: string, kind: SheetKind, label: string): string =>
    JSON.stringify([variable, kind, label])
  const byId = new Map<string, VariableRow | ItemRow>()
  /** The variables of the project. */
  const known = new Set<string>()
  /** The shown label of each stored entry the project does not have, by key. */
  const stored = new Map<string, string>()
  rows.forEach((r) => {
    if (r.kind === "gone") stored.set(r.key, r.shown)
    if (r.kind !== "variable" && r.kind !== "item") return
    if (r.kind === "variable") known.add(r.target.variable)
    const id = idOf(r.target.variable, kindOfRow(r), r.raw)
    if (!byId.has(id)) byId.set(id, r)
  })

  let unknown = 0
  /** The rows read for a variable the data may have, by key. */
  const waitingRows = new Map<string, VariableRow | ItemRow>()
  const read = new Set<string>()
  const decisions: Decision[] = []
  sheet.forEach((s) => {
    let row: VariableRow | ItemRow | null = byId.get(idOf(s.variable, s.kind, s.label)) ?? null
    if (!row && !dataLoaded && !known.has(s.variable)) {
      const made = waitingRowOf(s, stored)
      if (made) {
        row = waitingRows.get(made.key) ?? made
        waitingRows.set(row.key, row)
      }
    }
    if (!row) {
      unknown += 1
      return
    }
    if (read.has(row.key)) return
    read.add(row.key)
    const text = s.shown.trim()
    if (!text || text === row.raw) {
      if (row.shown !== null) decisions.push({ row, text: null, reason: null })
      return
    }
    if (text !== row.shown) decisions.push({ row, text, reason: null })
  })

  // Variables first: their names are the owners in the items' sentences.
  const owners = new Map<string, string>()
  ;[...rows, ...waitingRows.values()].forEach((r) => {
    if (r.kind === "variable") owners.set(r.target.variable, r.shown ?? r.raw)
  })
  decisions.forEach((d) => {
    if (d.row.kind !== "variable") return
    if (d.text === null) owners.set(d.row.target.variable, d.row.raw)
    else {
      d.reason = refusedShownLabel(d.text, [], null)
      if (!d.reason) owners.set(d.row.target.variable, d.text)
    }
  })

  // Items: the names after the import, every change planned.
  const taken = takenNames([...rows, ...waitingRows.values()])
  const items = decisions.filter(
    (d): d is Decision & { row: ItemRow } => d.row.kind === "item"
  )
  items.forEach((d) => taken.show(d.row, d.text ?? d.row.raw))
  const check = (d: Decision & { row: ItemRow }, text: string): string | null =>
    refusedShownLabel(text, [taken.clash(d.row, text)].filter((n): n is string => n !== null), {
      word: d.row.word,
      owner: owners.get(d.row.variable) ?? d.row.variable,
    })
  const refuse = (d: Decision & { row: ItemRow }, reason: string): void => {
    d.reason = reason
    taken.show(d.row, d.row.shown ?? d.row.raw)
  }
  items.forEach((d) => {
    if (d.text === null) return
    const reason = check(d, d.text)
    if (reason) refuse(d, reason)
  })
  // An item refused above is back under the name it has, which a name
  // accepted before it may now clash with: refuse that one too, until none
  // clashes (each pass refuses one more, so this ends).
  for (let again = true; again; ) {
    again = false
    items.forEach((d) => {
      if (d.text === null || d.reason !== null) return
      const reason = check(d, d.text)
      if (!reason) return
      refuse(d, reason)
      again = true
    })
  }

  const changes: LabelChange[] = []
  const refused: ImportPlan["refused"] = []
  let changed = 0
  let reset = 0
  let waiting = 0
  decisions.forEach(({ row, text, reason }) => {
    if (text === null) {
      changes.push({ target: row.target, shown: null })
      reset += 1
    } else if (reason) {
      refused.push({
        name: row.kind === "item" ? `${row.variable}: ${row.raw}` : row.raw,
        reason,
      })
    } else {
      changes.push({ target: row.target, shown: text })
      if (waitingRows.has(row.key)) waiting += 1
      else changed += 1
    }
  })
  return { changes, changed, reset, unknown, waiting, refused }
}

const plural = (n: number, one: string, many: string): string =>
  n === 1 ? `1 ${one}` : `${n.toLocaleString("en-US")} ${many}`

/**
 * What the drawer says after an upload: the counts, the rows kept for the
 * data, then the rows that named nothing of the project (`unknown`, those of
 * the sheet and those of the plan), then one line per refusal.
 */
export const describeImport = (plan: ImportPlan, unread = 0): string[] => {
  const unknown = plan.unknown + unread
  const nothing = plan.changed === 0 && plan.reset === 0
  return [
    ...(!nothing
      ? [
          `Read from the file: ${plural(plan.changed, "label changed", "labels changed")}, ${plan.reset.toLocaleString("en-US")} reset.`,
        ]
      : plan.waiting === 0
        ? ["No label changed."]
        : []),
    ...(plan.waiting > 0
      ? [
          plan.waiting === 1
            ? "1 row names a label the data may have, and waits for the data to be loaded."
            : `${plan.waiting.toLocaleString("en-US")} rows name labels the data may have, and wait for the data to be loaded.`,
        ]
      : []),
    ...(unknown > 0
      ? [
          unknown === 1
            ? "1 row names a label this project does not have and was not read."
            : `${unknown.toLocaleString("en-US")} rows name a label this project does not have and were not read.`,
        ]
      : []),
    ...plan.refused.map((r) => `${r.name}: ${r.reason}`),
  ]
}
